/** @format */

import { useState } from "react";
import { Helmet } from "react-helmet";
import { PopupModal } from "react-calendly";
import GoogleMap from "../../components/GoogleMap";
import { Calendar, Clock, MapPin } from "lucide-react";

export default function Showroom() {
	const [openBooking, setOpenBooking] = useState(false);

	return (
		<>
			<Helmet>
				<link rel="canonical" href={`https://offcutshades.com/showroom`} />
				<title>
					Shades & Blinds Showroom in Toronto: Custom Shade Gallery | Off Cut
					Shades
				</title>
				<meta
					name="description"
					content="Visit our shades and blinds showroom in Toronto. Off Cut Shades has a local showroom in Toronto so you can visualize our custom shades in your home."
				/>
				<script type="application/ld+json">
					{`{
						"@context": "https://schema.org",
						"@type": "BreadcrumbList",
						"itemListElement": [
							{
							"@type": "ListItem",
							"position": 1,
							"name": "Home",
							"item": "https://offcutshades.com"
							},
							{
							"@type": "ListItem",
							"position": 2,
							"name": "Showroom",
							"item": "https://offcutshades.com/showroom"
							}
						]
						}`}
				</script>
			</Helmet>
			<main className="bg-gray-50 min-h-screen py-12">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					{/* Hero Section */}
					<div className="text-center max-w-3xl mx-auto mb-16">
						<h1 className="text-4xl font-bold text-gray-900 mb-4">
							Our Showroom
						</h1>
						<p className="text-lg text-gray-600">
							Experience our window treatments in person and get expert guidance
							from our team of specialists.
						</p>
					</div>

					{/* Main Showroom Section */}
					<div className="flex bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-16">
						<div className="aspect-w-16 aspect-h-9">
							<img
								src="/images/showroom/1.jpg"
								alt="Our Showroom"
								className="h-[500px] w-auto object-cover"
							/>
						</div>
						<div className="p-8">
							<div className="max-w-3xl">
								<h2 className="text-2xl font-bold text-gray-900 mb-4">
									Visit Our Toronto Showroom
								</h2>
								<div className="prose text-gray-600">
									<p>
										Our Toronto Showroom represents the artistic and innovative
										vision behind Off Cuts Shades. Experience firsthand our wide
										range of options and styles to enhance your windows.
									</p>
									<p>
										Our knowledgeable staff will guide you through different
										materials, patterns, colors, and textures, helping you
										visualize the perfect solution for your home.
									</p>
								</div>

								<button
									onClick={() => setOpenBooking(true)}
									className="mt-8 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
								>
									<Calendar className="w-5 h-5" />
									Book a FREE Consultation
								</button>
							</div>
						</div>
					</div>

					{/* Hours and Location */}
					<div className="grid md:grid-cols-2 gap-8 mb-16">
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
							<div className="flex items-center gap-4 mb-6">
								<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
									<Clock className="w-6 h-6 text-primary" />
								</div>
								<h3 className="text-xl font-bold">Showroom Hours</h3>
							</div>
							<div className="space-y-2">
								{["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map(
									(day) => (
										<div key={day} className="flex justify-between">
											<span>{day}</span>
											<span>8:30 AM - 5:00 PM</span>
										</div>
									)
								)}
								{["Saturday", "Sunday"].map((day) => (
									<div key={day} className="flex justify-between text-gray-400">
										<span>{day}</span>
										<span>Closed</span>
									</div>
								))}
							</div>
						</div>

						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
							<div className="flex items-center gap-4 mb-6">
								<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
									<MapPin className="w-6 h-6 text-primary" />
								</div>
								<h3 className="text-xl font-bold">Location</h3>
							</div>
							<div className="h-[300px] rounded-lg overflow-hidden">
								<GoogleMap />
							</div>
							<div className="mt-4 text-center">
								<p className="text-gray-600">
									50 Hollinger Road, Toronto, ON M4B 3G5
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Calendly Modal */}
				<PopupModal
					url="https://calendly.com/offcutshades"
					rootElement={document.getElementById("root")}
					open={openBooking}
					onModalClose={() => setOpenBooking(false)}
					text="Schedule a Consultation"
					textColor="#ffffff"
					color="#00a2ff"
				/>
			</main>
		</>
	);
}
